import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  const navigationItems = [
    { name: 'Home', path: '/homepage-mediterranean-restaurant-experience', icon: 'Home' },
    { name: 'Menu', path: '/interactive-menu-culinary-discovery', icon: 'ChefHat' },
    { name: 'Our Story', path: '/our-story-heritage-authenticity', icon: 'Heart' },
    { name: 'Order Online', path: '/order-online-streamlined-experience', icon: 'ShoppingBag' },
    { name: 'Catering', path: '/catering-services-event-solutions', icon: 'Users' },
    { name: 'Contact', path: '/contact-location-welcoming-connection', icon: 'MapPin' }
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const isActivePath = (path) => {
    return location.pathname === path;
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-organic ${
      isScrolled 
        ? 'bg-background/95 backdrop-blur-warm shadow-warm' 
        : 'bg-background/90 backdrop-blur-sm'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link 
            to="/homepage-mediterranean-restaurant-experience" 
            className="flex items-center space-x-3 group"
            onClick={closeMenu}
          >
            <div className="relative">
              <svg 
                width="48" 
                height="48" 
                viewBox="0 0 48 48" 
                className="transition-organic group-hover:scale-105"
              >
                <circle 
                  cx="24" 
                  cy="24" 
                  r="22" 
                  fill="url(#logoGradient)" 
                  stroke="#8B4513" 
                  strokeWidth="2"
                />
                <path 
                  d="M16 20 L24 14 L32 20 L28 28 L20 28 Z" 
                  fill="#DAA520" 
                  opacity="0.9"
                />
                <circle cx="24" cy="22" r="3" fill="#8B4513" />
                <defs>
                  <linearGradient id="logoGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#C4622D" />
                    <stop offset="100%" stopColor="#8B4513" />
                  </linearGradient>
                </defs>
              </svg>
            </div>
            <div className="hidden sm:block">
              <h1 className="font-headline text-md font-bold text-primary group-hover:text-primary-600 transition-organic">
                Kebab Palace
              </h1>
           
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            {navigationItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center space-x-2 px-3 py-2 rounded-lg font-body font-medium transition-organic ${
                  isActivePath(item.path)
                    ? 'text-primary bg-primary-50 shadow-warm'
                    : 'text-text-primary hover:text-primary hover:bg-primary-50'
                }`}
              >
                <Icon name={item.icon} size={18} />
                <span>{item.name}</span>
              </Link>
            ))}
          </nav>

          {/* Trust Signals & CTA */}
          <div className="hidden lg:flex items-center space-x-6">
            <div className="flex items-center space-x-4 text-sm">
              <div className="flex items-center space-x-1 text-accent-600">
                <Icon name="Star" size={16} className="fill-current" />
                <span className="font-semibold">4.8/5</span>
              </div>
              <div className="flex items-center space-x-1 text-trust-600">
                <Icon name="Clock" size={16} />
                <span className="font-medium">15-20 min</span>
              </div>
            </div>
            <Link
              to="/order-online-streamlined-experience"
              className="btn-conversion animate-pulse-subtle"
            >
              Order Fresh Now
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMenu}
            className="lg:hidden p-2 rounded-lg text-text-primary hover:text-primary hover:bg-primary-50 transition-organic"
            aria-label="Toggle menu"
          >
            <Icon name={isMenuOpen ? "X" : "Menu"} size={24} />
          </button>
        </div>

        {/* Mobile Navigation */}
        <div className={`lg:hidden transition-organic overflow-hidden ${
          isMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}>
          <nav className="py-4 space-y-2 border-t border-primary-100">
            {navigationItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={closeMenu}
                className={`flex items-center space-x-3 px-4 py-3 rounded-lg font-body font-medium transition-organic ${
                  isActivePath(item.path)
                    ? 'text-primary bg-primary-50 shadow-warm'
                    : 'text-text-primary hover:text-primary hover:bg-primary-50'
                }`}
              >
                <Icon name={item.icon} size={20} />
                <span>{item.name}</span>
              </Link>
            ))}
            
            {/* Mobile Trust Signals & CTA */}
            <div className="px-4 py-3 space-y-3 border-t border-primary-100 mt-4">
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center space-x-1 text-accent-600">
                  <Icon name="Star" size={16} className="fill-current" />
                  <span className="font-semibold">4.8/5 Rating</span>
                </div>
                <div className="flex items-center space-x-1 text-trust-600">
                  <Icon name="Clock" size={16} />
                  <span className="font-medium">15-20 min</span>
                </div>
              </div>
              <Link
                to="/order-online-streamlined-experience"
                onClick={closeMenu}
                className="block w-full text-center btn-conversion"
              >
                Order Fresh Now
              </Link>
            </div>
          </nav>
        </div>
      </div>
    </header>
  );
};

export default Header;